import { useEffect, useMemo, useRef, useState } from 'react';
import {
  IconCamera,
  IconChevronRight,
  IconSearch,
  IconX,
} from 'design/icons';

import { fetchWithAuth } from '../utils/auth';

const GBP = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatGBP(value) {
  const numeric = Number(value);
  return value == null || !Number.isFinite(numeric) ? '—' : GBP.format(numeric);
}

function formatTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : DATE_TIME.format(date);
}

function plural(value, singular, pluralForm = `${singular}s`) {
  return `${value} ${Number(value) === 1 ? singular : pluralForm}`;
}

function displayLanguage(value) {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function slabGradeLabel(identity) {
  if (identity?.kind !== 'slab') return null;
  const company = String(identity?.gradingCompany || '').trim();
  const suppliedGrade = String(identity?.grade || '').trim();
  if (!company || !suppliedGrade) return 'Grade missing';

  const grade = suppliedGrade.toLowerCase().startsWith(company.toLowerCase())
    ? suppliedGrade.slice(company.length).trim()
    : suppliedGrade;
  return grade ? `${company} · Grade ${grade}` : `${company} · Grade missing`;
}

function displayConfidence(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return `${Math.round(numeric <= 1 ? numeric * 100 : numeric)}% recognition`;
}

async function responseData(response) {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(
      payload?.error?.message || 'CardCam sold comps are unavailable.'
    );
  }
  return payload?.data;
}

function SaleImage({ image, label, className = '', priority = false }) {
  const [captureSource, setCaptureSource] = useState('');
  const [failed, setFailed] = useState(false);
  const captureID = image?.type === 'capture' ? image.captureID : '';
  const directSource =
    image?.type === 'ace' || image?.type === 'catalogue' ? image.url : '';

  useEffect(() => {
    setFailed(false);
  }, [captureID, directSource]);

  useEffect(() => {
    if (!captureID) {
      setCaptureSource('');
      return undefined;
    }

    let active = true;
    let objectURL = '';
    setCaptureSource('');
    fetchWithAuth(
      `/api/cardcam/v2/captures/image?id=${encodeURIComponent(captureID)}`
    )
      .then(async (response) => {
        if (!response.ok) throw new Error('Capture image is unavailable.');
        return response.blob();
      })
      .then((blob) => {
        if (!active) return;
        objectURL = URL.createObjectURL(blob);
        setCaptureSource(objectURL);
      })
      .catch(() => {
        if (active) setFailed(true);
      });

    return () => {
      active = false;
      if (objectURL) URL.revokeObjectURL(objectURL);
    };
  }, [captureID]);

  const source = directSource || captureSource;
  if (!source || failed) {
    return (
      <div className={`cardcam-gallery-image-state ${className}`}>
        <IconCamera size={28} aria-hidden="true" />
        <span>{captureID && !failed ? 'Loading image…' : 'Image unavailable'}</span>
      </div>
    );
  }

  return (
    <img
      className={`cardcam-gallery-image ${className}`}
      src={source}
      alt={label}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchpriority={priority ? 'high' : 'low'}
      onError={() => setFailed(true)}
    />
  );
}

function GalleryMetric({ label, value, detail }) {
  return (
    <div className="cardcam-gallery-metric">
      <span className="rc-label">{label}</span>
      <strong>{value}</strong>
      {detail ? <small>{detail}</small> : null}
    </div>
  );
}

function CardIdentity({ identity, compact = false }) {
  const confidence = displayConfidence(identity?.confidence);
  const gradeLabel = slabGradeLabel(identity);
  const discriminators = [
    identity?.variant,
    identity?.edition,
    identity?.finish,
    identity?.slabQualifier,
  ].filter((value, index, values) =>
    value && values.findIndex(
      (candidate) => candidate?.toLowerCase() === value.toLowerCase()
    ) === index
  );
  return (
    <div className={compact ? 'cardcam-gallery-identity-compact' : ''}>
      <div className="cardcam-gallery-identity-title">
        <strong>{identity?.name || 'Unidentified card'}</strong>
        {gradeLabel ? (
          <span
            className={`cardcam-gallery-grade-badge${
              gradeLabel === 'Grade missing'
                ? ' cardcam-gallery-grade-badge-missing'
                : ''
            }`}
          >
            {gradeLabel}
          </span>
        ) : null}
      </div>
      <span>
        {[
          identity?.number,
          identity?.setName,
          ...discriminators,
        ].filter(Boolean).join(' · ') || 'Exact printing'}
      </span>
      <small>
        {[
          displayLanguage(identity?.language),
          identity?.kind === 'raw' ? 'Single card' : 'Graded slab',
          confidence,
        ]
          .filter(Boolean)
          .join(' · ')}
      </small>
    </div>
  );
}

export default function CardCamSaleGallery() {
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const [search, setSearch] = useState({
    submittedQuery: '',
    results: [],
    summary: null,
    loading: false,
    error: '',
    searched: false,
  });
  const [detail, setDetail] = useState({
    selected: null,
    card: null,
    stats: null,
    sales: [],
    loading: false,
    error: '',
  });
  const searchControllerRef = useRef(null);
  const detailControllerRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(
    () => () => {
      searchControllerRef.current?.abort();
      detailControllerRef.current?.abort();
    },
    []
  );

  const sortedSales = useMemo(
    () =>
      [...(detail.sales || [])].sort(
        (left, right) =>
          new Date(right.soldAt || 0).getTime() -
          new Date(left.soldAt || 0).getTime()
      ),
    [detail.sales]
  );

  async function runSearch(nextQuery, nextKind = kind) {
    const trimmedQuery = nextQuery.trim();
    if (!trimmedQuery) return;

    searchControllerRef.current?.abort();
    const controller = new AbortController();
    searchControllerRef.current = controller;
    setSearch((current) => ({
      ...current,
      submittedQuery: trimmedQuery,
      loading: true,
      error: '',
      searched: true,
    }));

    try {
      const parameters = new URLSearchParams({
        q: trimmedQuery,
        kind: nextKind,
        limit: '50',
      });
      const response = await fetchWithAuth(
        `/api/cardcam/v2/captures/search?${parameters.toString()}`,
        { signal: controller.signal }
      );
      const data = await responseData(response);
      if (controller.signal.aborted) return;
      setSearch({
        submittedQuery: trimmedQuery,
        results: data?.redacted ? [] : data?.results || [],
        summary: data?.redacted ? null : data?.summary || null,
        loading: false,
        error: data?.redacted
          ? 'Sold-comp search is available in admin view.'
          : '',
        searched: true,
      });
    } catch (requestError) {
      if (requestError?.name === 'AbortError') return;
      setSearch({
        submittedQuery: trimmedQuery,
        results: [],
        summary: null,
        loading: false,
        error: requestError.message,
        searched: true,
      });
    }
  }

  function submitSearch(event) {
    event.preventDefault();
    runSearch(query);
  }

  function changeKind(nextKind) {
    setKind(nextKind);
    const activeQuery = query.trim() || search.submittedQuery;
    if (search.searched && activeQuery) {
      runSearch(activeQuery, nextKind);
    }
  }

  function clearSearch() {
    searchControllerRef.current?.abort();
    setQuery('');
    setSearch({
      submittedQuery: '',
      results: [],
      summary: null,
      loading: false,
      error: '',
      searched: false,
    });
  }

  async function openDetails(selected) {
    detailControllerRef.current?.abort();
    const controller = new AbortController();
    detailControllerRef.current = controller;
    setDetail({
      selected,
      card: selected,
      stats: selected.stats || null,
      sales: [],
      loading: true,
      error: '',
    });
    if (!dialogRef.current?.open) dialogRef.current?.showModal();

    try {
      const parameters = new URLSearchParams({
        merchandiseKey: selected.merchandiseKey,
        limit: '100',
      });
      const response = await fetchWithAuth(
        `/api/cardcam/v2/captures/sales-history?${parameters.toString()}`,
        { signal: controller.signal }
      );
      const data = await responseData(response);
      if (controller.signal.aborted) return;
      setDetail({
        selected,
        card: data?.card || selected,
        stats: data?.stats || selected.stats || null,
        sales: data?.sales || [],
        loading: false,
        error: data?.redacted
          ? 'Sale history is available in admin view.'
          : '',
      });
    } catch (requestError) {
      if (requestError?.name === 'AbortError') return;
      setDetail((current) => ({
        ...current,
        loading: false,
        error: requestError.message,
      }));
    }
  }

  function closeDetails() {
    detailControllerRef.current?.abort();
    if (dialogRef.current?.open) dialogRef.current.close();
  }

  const summary = search.summary || {};
  const groupCount = summary.groupCount ?? search.results.length;
  const saleCount =
    summary.saleCount ??
    search.results.reduce((total, card) => total + (card.stats?.saleCount || 0), 0);
  const detailCard = detail.card || detail.selected;
  const detailIdentity = detailCard?.identity || detailCard || {};
  const detailImage = detailCard?.image || detail.selected?.image;
  const detailStats = detail.stats || detail.selected?.stats || {};

  return (
    <section
      className="cardcam-sales-search"
      aria-label="Search paid CardCam sales"
      aria-busy={search.loading}
    >
      <form className="cardcam-sales-search-form" onSubmit={submitSearch}>
        <div className="cardcam-sales-search-copy">
          <strong>Find sold comps</strong>
          <small>Search in English · English &amp; Japanese cards</small>
        </div>
        <label className="cardcam-sales-search-input">
          <IconSearch size={17} aria-hidden="true" />
          <span className="cardcam-visually-hidden">
            English card name or card number
          </span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Pikachu, Latios or 070/064"
            maxLength={120}
            autoComplete="off"
          />
        </label>
        <fieldset className="cardcam-sales-kind-filter">
          <legend className="cardcam-visually-hidden">Card type</legend>
          {[
            ['all', 'All'],
            ['slab', 'Slabs'],
            ['raw', 'Singles'],
          ].map(([value, label]) => (
            <label key={value}>
              <input
                type="radio"
                name="cardcam-sale-kind"
                value={value}
                checked={kind === value}
                onChange={() => changeKind(value)}
              />
              <span>{label}</span>
            </label>
          ))}
        </fieldset>
        <button
          type="submit"
          className="cardcam-sales-search-submit"
          disabled={search.loading || !query.trim()}
        >
          {search.loading ? 'Searching…' : 'Search sales'}
        </button>
        {search.searched || query ? (
          <button
            type="button"
            className="cardcam-sales-search-clear"
            onClick={clearSearch}
            aria-label="Clear sale search"
          >
            <IconX size={16} aria-hidden="true" />
            Clear
          </button>
        ) : null}
      </form>

      <div aria-live="polite">
        {search.error ? (
          <p className="cardcam-sales-search-state cardcam-sales-search-error">
            {search.error}
          </p>
        ) : search.loading ? (
          <p className="cardcam-sales-search-state">
            Finding exact cards and paid sale history…
          </p>
        ) : search.searched ? (
          search.results.length ? (
            <div className="cardcam-sales-results">
              <div className="cardcam-sales-results-heading">
                <div>
                  <strong>
                    {plural(groupCount, 'exact card')} · {plural(saleCount, 'paid sale')}
                    {summary.channelCount != null
                      ? ` across ${plural(summary.channelCount, 'channel')}`
                      : ''}
                  </strong>
                  <small>
                    Payment-linked CardCam records · older legacy screenshot logs excluded
                  </small>
                </div>
                <span>“{search.submittedQuery}”</span>
              </div>
              <div className="cardcam-sales-gallery">
                {search.results.map((card, index) => {
                  const identity = card.identity || {};
                  const stats = card.stats || {};
                  return (
                    <button
                      type="button"
                      className="cardcam-sales-gallery-card"
                      key={card.merchandiseKey}
                      onClick={() => openDetails(card)}
                      aria-label={`Open sale history for ${identity.name || 'card'} ${
                        identity.number || ''
                      }${slabGradeLabel(identity) ? `, ${slabGradeLabel(identity)}` : ''}`}
                    >
                      <div className="cardcam-sales-gallery-image-wrap">
                        <SaleImage
                          image={card.image}
                          priority={index < 3}
                          label={`${identity.name || 'Card'} ${
                            identity.number || ''
                          } sold-card image`}
                        />
                        <span
                          className={`cardcam-sales-gallery-review cardcam-sales-gallery-review-${
                            card.reviewStatus === 'confirmed'
                              ? 'confirmed'
                              : 'unreviewed'
                          }`}
                        >
                          {card.reviewStatus === 'confirmed'
                            ? 'Confirmed'
                            : 'Unreviewed'}
                        </span>
                        <span className="cardcam-sales-gallery-count">
                          {plural(stats.saleCount || 0, 'sale')}
                        </span>
                      </div>
                      <div className="cardcam-sales-gallery-body">
                        <CardIdentity identity={identity} compact />
                        <div className="cardcam-sales-gallery-stats">
                          <GalleryMetric
                            label="Latest"
                            value={formatGBP(stats.latestHammerGBP)}
                          />
                          <GalleryMetric
                            label="CardCam avg"
                            value={formatGBP(stats.averageHammerGBP)}
                          />
                          <GalleryMetric
                            label="CardCam median"
                            value={formatGBP(stats.medianHammerGBP)}
                          />
                          <GalleryMetric
                            label="Last 30 days"
                            value={formatGBP(stats.average30DayGBP)}
                            detail={plural(stats.last30DayCount || 0, 'sale')}
                          />
                        </div>
                        <div className="cardcam-sales-gallery-footer">
                          <span>
                            Last sold {formatTime(card.latestSale?.soldAt)}
                            {card.latestSale?.sellerUsername
                              ? ` · ${card.latestSale.sellerUsername}`
                              : ''}
                          </span>
                          <span>
                            History <IconChevronRight size={15} aria-hidden="true" />
                          </span>
                        </div>
                        {stats.repeatedCertificationCount > 0 ? (
                          <div className="cardcam-sales-gallery-warning">
                            Certificate reused · check images
                          </div>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="cardcam-sales-search-state">
              No paid {kind === 'all' ? 'card' : kind === 'raw' ? 'single' : 'slab'} sale
              matched “{search.submittedQuery}”.
            </p>
          )
        ) : null}
      </div>

      <dialog
        ref={dialogRef}
        className="cardcam-sales-dialog"
        aria-labelledby="cardcam-sales-dialog-title"
        onClose={() =>
          setDetail((current) => ({ ...current, selected: null }))
        }
        onClick={(event) => {
          if (event.target === event.currentTarget) closeDetails();
        }}
      >
        {detail.selected ? (
          <div className="cardcam-sales-dialog-panel">
            <header className="cardcam-sales-dialog-header">
              <div>
                <span className="rc-eyebrow">Paid sale history</span>
                <h2 id="cardcam-sales-dialog-title">
                  {detailIdentity.name || 'Card history'}
                </h2>
              </div>
              <button type="button" onClick={closeDetails} aria-label="Close sale history">
                <IconX size={20} aria-hidden="true" />
              </button>
            </header>

            <div className="cardcam-sales-dialog-card">
              <div className="cardcam-sales-dialog-image-wrap">
                <SaleImage
                  image={detailImage}
                  priority
                  label={`${detailIdentity.name || 'Card'} ${
                    detailIdentity.number || ''
                  } sold-card image`}
                />
              </div>
              <CardIdentity identity={detailIdentity} />
            </div>

            <div className="cardcam-sales-dialog-stats">
              <GalleryMetric
                label="Latest hammer"
                value={formatGBP(detailStats.latestHammerGBP)}
                detail="Most recent paid sale"
              />
              <GalleryMetric
                label="Recorded average"
                value={formatGBP(detailStats.averageHammerGBP)}
                detail={plural(detailStats.saleCount || 0, 'sale')}
              />
              <GalleryMetric
                label="Recorded median"
                value={formatGBP(detailStats.medianHammerGBP)}
                detail="Less affected by unusual bids"
              />
              <GalleryMetric
                label="30-day average"
                value={formatGBP(detailStats.average30DayGBP)}
                detail={`${plural(
                  detailStats.last30DayCount || 0,
                  'sale'
                )} · all channels`}
              />
              <GalleryMetric
                label="Last 3 streams"
                value={formatGBP(detailStats.averageLastThreeStreamsGBP)}
                detail={`${plural(
                  detailStats.lastThreeStreamSaleCount || 0,
                  'sale'
                )} in ${plural(
                  detailStats.lastThreeStreamCount || 0,
                  'stream'
                )}`}
              />
              <GalleryMetric
                label="Channels"
                value={String(detailStats.channelCount || 0)}
                detail="Accounts with a paid sale"
              />
            </div>

            {detailStats.repeatedCertificationCount > 0 ? (
              <div className="cardcam-sales-dialog-warning" role="note">
                <strong>Recognition warning</strong>
                <span>
                  {plural(
                    detailStats.repeatedCertificationCount,
                    'sale record'
                  )}{' '}
                  reused an ACE certificate in this exact-card group. Review
                  the images before relying on the average.
                </span>
              </div>
            ) : null}

            <section className="cardcam-sales-dialog-history" aria-busy={detail.loading}>
              <div className="cardcam-sales-dialog-history-heading">
                <h3>Recorded CardCam sale history</h3>
                <span>
                  {detailStats.saleCount > sortedSales.length
                    ? `Latest ${sortedSales.length} of ${detailStats.saleCount}`
                    : `${sortedSales.length} recorded`}
                  {' · '}newest first · across all channels
                </span>
              </div>
              {detail.loading ? (
                <p className="cardcam-sales-search-state">
                  Loading exact sale history…
                </p>
              ) : detail.error ? (
                <p className="cardcam-sales-search-state cardcam-sales-search-error">
                  {detail.error}
                </p>
              ) : sortedSales.length ? (
                <ol>
                  {sortedSales.map((sale, index) => {
                    const confidence = displayConfidence(sale.confidence);
                    return (
                      <li
                        key={
                          sale.id ||
                          `${sale.soldAt || 'sale'}-${sale.sellerUsername || 'account'}-${index}`
                        }
                      >
                        <div>
                          <strong>{formatGBP(sale.hammerGBP)}</strong>
                          <span>{sale.sellerUsername || 'Unknown account'}</span>
                        </div>
                        <div>
                          <strong>{formatTime(sale.soldAt)}</strong>
                          <span>
                            {sale.reviewStatus === 'confirmed'
                              ? 'Confirmed identity'
                              : 'Unreviewed identity'}
                            {confidence ? ` · ${confidence}` : ''}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <p className="cardcam-sales-search-state">
                  No paid sale history is available for this exact card.
                </p>
              )}
            </section>
          </div>
        ) : null}
      </dialog>
    </section>
  );
}
